import { useEffect, useRef, useState } from "react";

import { gameDisplayLabel } from "../../lib/gamePalette.js";

export interface BarChartStackSegment {
  value: number;
  fill: string;
  /** Shown in segment tooltip and on the bar when tall enough. */
  label?: string;
}

export interface BarChartItem {
  label: string;
  value: number;
  /**
   * When set, the bar is drawn as stacked segments bottom-to-top; segment `value` sums should
   * equal `value` (total height uses `value` for scale).
   */
  stack?: BarChartStackSegment[];
  /** Segments drawn downward from the baseline (e.g. abandoned counts below completions). */
  stackBelow?: BarChartStackSegment[];
  /** When set, used as bar fill instead of the default blue. */
  fill?: string;
  /** When set, replaces the auto-generated text above the bar (counts / % / ms). */
  valueLabel?: string;
  /**
   * Multi-line label above the bar (e.g. one metric per line). When set, overrides **`valueLabel`**
   * for display and uses tighter line spacing so labels sit just above the bar top.
   */
  valueLabelLines?: string[];
  /** Override x-axis label colour (default `#9eb2d7`). */
  axisLabelFill?: string;
  /** Override x-axis label font-weight (e.g. `700` for weekends). */
  axisLabelFontWeight?: number | string;
  /**
   * Multi-line x-axis category label (e.g. table + venue). When set, overrides **`label`** for
   * the axis (tooltip / click still use `label`). With **`rotateAxisLabels`**, lines are
   * end-anchored (top of the rotated block against the plot).
   */
  axisLabelLines?: string[];
  /**
   * Optional horizontal marker across this bar (e.g. same weekday last week). Drawn as a dotted
   * line at the given value on the bar scale; included in auto y-scale when present.
   */
  referenceValue?: number;
  /**
   * Optional second series for the same category. Drawn as a sibling bar (grouped) and scaled
   * independently when **`showSecondaryYAxis`** is set (e.g. £ vs £/uptime-h).
   */
  secondaryValue?: number;
  /** Fill for the secondary bar (defaults to a faded variant of the primary fill / blue). */
  secondaryFill?: string;
}

interface BarChartProps {
  data: BarChartItem[];
  compact?: boolean;
  percent?: boolean;
  fillBars?: boolean;
  highlightIndex?: number;
  highlightLabel?: string;
  /**
   * When set, x-axis labels use the bar `label` string as-is (trimmed), not `gameLabel()` title-casing.
   * Use for non–game-route charts (e.g. Top Issues).
   */
  verbatimAxisLabels?: boolean;
  /**
   * Rotate x-axis labels −90° so the end of the string sits next to the plot (e.g. `31/7 F`
   * puts **F** against the bars). Increases bottom margin for the rotated text.
   */
  rotateAxisLabels?: boolean;
  /** Max characters for the bottom x-axis label (after optional `gameLabel`). */
  axisLabelMaxChars?: number;
  /**
   * Minimum bottom margin for x-axis labels (viewBox px). Use with **`rotateAxisLabels`** to
   * show longer rotated strings without growing the CSS box — pair with a lower **`plotCssHeight`**
   * if needed so total height stays put.
   */
  axisReserveMin?: number;
  /** Cap bar width when there are few categories (bars stay left-aligned). */
  maxBarWidth?: number;
  /** Override plot area height (px); mainly for taller bars in compact narrow columns. */
  plotCssHeight?: number;
  /** Narrower viewBox width (avoids shrinking labels in tight columns). */
  viewBoxWidth?: number;
  /** Size SVG height from viewBox aspect ratio (no letterboxing below the plot). */
  heightAuto?: boolean;
  /**
   * Size the viewBox to the container width and the CSS height to the viewBox height so axes
   * sit on the card edges (no `meet` letterboxing). Use when a second Y-axis must stay at the
   * end of the bars.
   */
  fillWidth?: boolean;
  /**
   * Draw the numbers above each bar. Off for dense day/week charts where labels overlap.
   * When false, the reserved label band is collapsed.
   */
  showValueLabels?: boolean;
  /**
   * Fixed positive scale maximum for bar heights (e.g. **24** for hours-in-day charts so
   * a partial “today” bar does not stretch to full height when it is the tallest value).
   */
  yMax?: number;
  /** Coloured key above the plot (e.g. stacked segment meanings). */
  legend?: Array<{
    label: string;
    fill?: string;
    /** When set (no fill swatch), draws a sample stroke — e.g. prior-week dotted line. */
    stroke?: string;
    strokeDasharray?: string;
  }>;
  /** Font size for the count / value label above each bar (default 10). */
  valueLabelFontSize?: number;
  /** Gap between bar top and value label baseline in px (default 4). */
  valueLabelPad?: number;
  /**
   * Title to the **left** of the value-label row (counts above bars), e.g. **Samples** or
   * **Uptime**. Count labels always sit in a reserved band **above** the plot — never on bar tops.
   */
  valueLabelAxisTitle?: string;
  /**
   * Optional meta row drawn above the value-label title (e.g. **3 tables** for fleet capacity mix).
   */
  valueLabelAxisMeta?: string;
  /**
   * Title to the **left** of the x-axis category labels (e.g. **°C**). Use with
   * **`verbatimAxisLabels`** so hyphens stay (`55-60`, not `55 60` from game-label formatting).
   */
  axisLabelAxisTitle?: string;
  /** When true, do not draw numeric/name labels inside stacked segments. */
  hideStackSegmentLabels?: boolean;
  /**
   * When true with `stack`, each segment is drawn from the baseline (overlay / nested funnel),
   * height ∝ segment value vs the bar scale (`value` / chart max). First stack entry is drawn
   * behind (typically the largest stage). Default additive stacking.
   */
  stackOverlay?: boolean;
  /**
   * Draw a left Y-axis with tick labels and faint horizontal grid lines. Extends the scale to a
   * nice round max so the top tick sits at the top of the plot.
   */
  showYAxis?: boolean;
  /** Format Y-axis tick values (default: integers, or one decimal when max &lt; 10). */
  formatYAxisTick?: (value: number) => string;
  /**
   * Draw a right Y-axis for **`secondaryValue`** (independent scale). Requires at least one item
   * with a finite **`secondaryValue`**.
   */
  showSecondaryYAxis?: boolean;
  /** Format right Y-axis tick values (defaults to the same rules as the primary axis). */
  formatSecondaryYAxisTick?: (value: number) => string;
  /** Native SVG tooltip on each bar (or segment when `segmentIndex` is set on stacked bars). */
  barTooltip?: string | ((item: BarChartItem, index: number, segmentIndex?: number) => string);
  /**
   * Click: open in place. Middle-click or Ctrl/Cmd+click (see `e`): open in a new tab — see
   * `packages/analytics-app/src/lib/dashboardNav.ts`.
   */
  onBarClick?: (
    item: BarChartItem,
    index: number,
    segmentIndex?: number,
    e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey">,
  ) => void;
}

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function gameLabel(v: string): string {
  return gameDisplayLabel(v);
}

function trimLabel(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}.` : s;
}

/** Nice round Y ticks from 0 to ≥ maxValue (inclusive). */
function niceYAxisTicks(maxValue: number, approxCount = 4): number[] {
  if (!(maxValue > 0) || !Number.isFinite(maxValue)) return [0];
  const rough = maxValue / Math.max(1, approxCount);
  const pow = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / pow;
  let step: number;
  if (normalized <= 1) step = pow;
  else if (normalized <= 2) step = 2 * pow;
  else if (normalized <= 5) step = 5 * pow;
  else step = 10 * pow;
  const niceMax = Math.ceil(maxValue / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= niceMax + step * 1e-9; v += step) {
    ticks.push(Math.round(v * 1e9) / 1e9);
  }
  return ticks;
}

function defaultFormatYAxisTick(value: number, scaleMax: number): string {
  if (scaleMax > 0 && scaleMax < 10) {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }
  if (value >= 1000) return `${Math.round(value).toLocaleString()}`;
  return String(Math.round(value));
}

export function BarChart({
  data,
  compact = false,
  percent = false,
  fillBars = false,
  highlightIndex,
  highlightLabel,
  verbatimAxisLabels = false,
  rotateAxisLabels = false,
  axisLabelMaxChars = 8,
  axisReserveMin,
  maxBarWidth,
  plotCssHeight,
  viewBoxWidth,
  heightAuto = false,
  fillWidth = false,
  showValueLabels = true,
  yMax,
  legend,
  valueLabelFontSize = 10,
  valueLabelPad = 4,
  valueLabelAxisTitle,
  valueLabelAxisMeta,
  axisLabelAxisTitle,
  hideStackSegmentLabels = false,
  stackOverlay = false,
  showYAxis = false,
  formatYAxisTick,
  showSecondaryYAxis = false,
  formatSecondaryYAxisTick,
  barTooltip,
  onBarClick,
}: BarChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState(0);
  useEffect(() => {
    if (!fillWidth) return;
    const el = wrapRef.current;
    if (!el) return;
    const apply = (w: number) => {
      const next = Math.floor(w);
      if (next > 0) setMeasuredWidth((prev) => (prev === next ? prev : next));
    };
    apply(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      apply(entries[0]?.contentRect.width ?? 0);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillWidth]);

  if (data.length === 0) {
    return <p className="py-5 text-[13px] text-text-secondary">No data</p>;
  }

  const width = fillWidth && measuredWidth > 0 ? measuredWidth : (viewBoxWidth ?? 520);
  const maxAxisLines = Math.max(1, ...data.map((r) => r.axisLabelLines?.length ?? 1));
  const axisLineHeight = 11;
  /** Rotated multi-line labels need width for each column (line). */
  const axisReserveBase = rotateAxisLabels
    ? Math.max(52, 28 + maxAxisLines * axisLineHeight)
    : maxAxisLines > 1
      ? 8 + maxAxisLines * axisLineHeight
      : 20;
  const axisReserve =
    axisReserveMin != null && Number.isFinite(axisReserveMin)
      ? Math.max(axisReserveBase, axisReserveMin)
      : axisReserveBase;
  const valueMetaBand = valueLabelAxisMeta ? 14 : 0;
  const maxValueLabelLines = Math.max(
    1,
    ...data.map((r) => {
      const n = r.valueLabelLines?.filter((s) => s.trim() !== "").length ?? 0;
      return n > 0 ? n : 1;
    }),
  );
  const valueLabelLineHeight = valueLabelFontSize + 1;
  /** Always reserve space above the plot so count labels never sit on full-height bar tops. */
  const valueLabelBand = showValueLabels
    ? valueLabelFontSize +
      valueLabelPad +
      8 +
      Math.max(0, maxValueLabelLines - 1) * valueLabelLineHeight
    : 0;
  /** Overlay funnels need extra plot height so nested stage rings stay readable. */
  const defaultPlotCss = compact ? 200 : stackOverlay ? 280 : 220;
  const defaultSvgPlotCss = compact ? 170 : stackOverlay ? 260 : 205;
  const basePlotCss = plotCssHeight ?? defaultPlotCss;
  const height =
    basePlotCss +
    (rotateAxisLabels
      ? 32 + Math.max(0, maxAxisLines - 1) * axisLineHeight
      : maxAxisLines > 1
        ? (maxAxisLines - 1) * axisLineHeight
        : 0) +
    valueMetaBand +
    valueLabelBand;
  const legendTop = legend?.length ? 34 : 8;
  const valueMetaY = legendTop + (valueLabelAxisMeta ? 10 : 0);
  const valueLabelsOriginY = legendTop + valueMetaBand;
  /** Vertically centre the side title across multi-line value labels. */
  const valueTitleY =
    valueLabelsOriginY +
    valueLabelFontSize +
    ((maxValueLabelLines - 1) * valueLabelLineHeight) / 2;
  const chartTop = legendTop + valueMetaBand + valueLabelBand;
  const plotBottom = height - axisReserve;
  const plotHeight = plotBottom - chartTop;
  const svgPlotCss =
    plotCssHeight !== undefined
      ? Math.max(120, plotCssHeight - (defaultPlotCss - defaultSvgPlotCss))
      : defaultSvgPlotCss;
  const svgCssHeight =
    svgPlotCss +
    (rotateAxisLabels
      ? 28 + Math.max(0, maxAxisLines - 1) * axisLineHeight
      : maxAxisLines > 1
        ? (maxAxisLines - 1) * axisLineHeight + 8
        : 0) +
    valueMetaBand +
    valueLabelBand;
  const barPosTotals = data.map((r) => {
    if (r.stack?.length) {
      if (stackOverlay) {
        // Overlay segments share a baseline; scale by bar value / tallest stage, not the sum
        // (summing stages made funnel bars ~half height with labels floating above).
        return Math.max(r.value, ...r.stack.map((s) => s.value), 0);
      }
      return r.stack.reduce((s, seg) => s + seg.value, 0);
    }
    return r.value;
  });
  const barNegTotals = data.map((r) => {
    if (r.stackBelow?.length) return r.stackBelow.reduce((s, seg) => s + seg.value, 0);
    return 0;
  });
  const hasDiverging = barNegTotals.some((n) => n > 0);
  const refMax = Math.max(
    0,
    ...data.map((r) =>
      r.referenceValue != null && Number.isFinite(r.referenceValue) ? r.referenceValue : 0,
    ),
  );
  const dataMaxPos = percent
    ? Math.max(0.0001, ...data.map((r) => r.value))
    : Math.max(1, ...barPosTotals, refMax);
  let maxPos =
    yMax != null && Number.isFinite(yMax) && yMax > 0
      ? Math.max(yMax, dataMaxPos)
      : dataMaxPos;
  const yAxisTicks =
    showYAxis && !hasDiverging && !percent ? niceYAxisTicks(maxPos, 4) : [];
  if (yAxisTicks.length > 0) {
    maxPos = Math.max(maxPos, yAxisTicks[yAxisTicks.length - 1]!);
  }
  const hasSecondarySeries = data.some(
    (r) => r.secondaryValue != null && Number.isFinite(r.secondaryValue) && r.secondaryValue > 0,
  );
  const secondaryDataMax = Math.max(
    0.0001,
    ...data.map((r) =>
      r.secondaryValue != null && Number.isFinite(r.secondaryValue) ? r.secondaryValue : 0,
    ),
  );
  let secondaryMax = secondaryDataMax;
  const secondaryYAxisTicks =
    showSecondaryYAxis && hasSecondarySeries && !hasDiverging && !percent
      ? niceYAxisTicks(secondaryMax, 4)
      : [];
  if (secondaryYAxisTicks.length > 0) {
    secondaryMax = Math.max(secondaryMax, secondaryYAxisTicks[secondaryYAxisTicks.length - 1]!);
  }
  const maxNeg = hasDiverging ? Math.max(1, ...barNegTotals) : 0;
  const posPlotHeight = hasDiverging
    ? (plotHeight * maxPos) / Math.max(maxPos + maxNeg, 1)
    : plotHeight;
  const negPlotHeight = hasDiverging ? plotHeight - posPlotHeight : 0;
  const baselineY = hasDiverging ? chartTop + posPlotHeight : plotBottom;
  const longestSideTitle = Math.max(
    valueLabelAxisTitle?.length ?? 0,
    axisLabelAxisTitle?.length ?? 0,
  );
  const yTickLabels = yAxisTicks.map((v) =>
    formatYAxisTick ? formatYAxisTick(v) : defaultFormatYAxisTick(v, maxPos),
  );
  const longestYTick = yTickLabels.reduce((n, s) => Math.max(n, s.length), 0);
  const yAxisPad = showYAxis && yAxisTicks.length > 0 ? Math.max(28, longestYTick * 6.2 + 10) : 0;
  const secondaryYTickLabels = secondaryYAxisTicks.map((v) =>
    formatSecondaryYAxisTick
      ? formatSecondaryYAxisTick(v)
      : defaultFormatYAxisTick(v, secondaryMax),
  );
  const longestSecondaryYTick = secondaryYTickLabels.reduce((n, s) => Math.max(n, s.length), 0);
  const secondaryYAxisPad =
    secondaryYAxisTicks.length > 0 ? Math.max(28, longestSecondaryYTick * 6.2 + 10) : 0;
  const chartPadRight = Math.max(8, secondaryYAxisPad);
  const chartPadLeft = Math.max(
    valueLabelAxisTitle || axisLabelAxisTitle
      ? Math.max(40, Math.ceil(longestSideTitle * 6.2) + 8)
      : 8,
    yAxisPad,
  );
  const useGroupedSecondary = hasSecondarySeries && !hasDiverging;
  const innerWidth = Math.max(1, width - chartPadLeft - chartPadRight);
  const categoryCount = Math.max(1, data.length);
  let gap = fillBars ? 2 : 8;
  if (categoryCount > 1 && gap * (categoryCount - 1) >= innerWidth) {
    gap = 0;
  } else if (categoryCount > 1 && gap * (categoryCount - 1) > innerWidth * 0.35) {
    gap = Math.max(0, (innerWidth * 0.2) / (categoryCount - 1));
  }
  const rawBarW = (innerWidth - gap * Math.max(0, categoryCount - 1)) / categoryCount;
  const barW =
    maxBarWidth !== undefined ? Math.min(maxBarWidth, Math.max(0.5, rawBarW)) : Math.max(0.5, rawBarW);
  const barsStartX = chartPadLeft;
  /** Last bar ends here — the right axis is pinned to this edge, not a fixed viewBox coordinate. */
  const barsEndX = barsStartX + categoryCount * barW + Math.max(0, categoryCount - 1) * gap;
  const axisTitleY = rotateAxisLabels ? plotBottom + 4 : height - 6;

  const svg = (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={fillWidth ? height : heightAuto ? undefined : svgCssHeight}
      className={heightAuto || fillWidth ? "block w-full" : undefined}
      preserveAspectRatio="xMidYMid meet"
    >
      {valueLabelAxisMeta ? (
        <text x={chartPadLeft} y={valueMetaY} fill="#9eb2d7" fontSize="11" fontWeight={600}>
          {valueLabelAxisMeta}
        </text>
      ) : null}
      {valueLabelAxisTitle ? (
        <text x={4} y={valueTitleY} fill="#9eb2d7" fontSize="10" fontWeight={600} textAnchor="start">
          {valueLabelAxisTitle}
        </text>
      ) : null}
      {axisLabelAxisTitle ? (
        <text
          x={4}
          y={axisTitleY}
          fill="#9eb2d7"
          fontSize="10"
          fontWeight={600}
          textAnchor="start"
          dominantBaseline={rotateAxisLabels ? "middle" : undefined}
        >
          {axisLabelAxisTitle}
        </text>
      ) : null}
      {yAxisTicks.length > 0 ? (
        <g>
          {yAxisTicks.map((tick, ti) => {
            const y = baselineY - (tick / maxPos) * posPlotHeight;
            const label = yTickLabels[ti] ?? String(tick);
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={chartPadLeft}
                  y1={y}
                  x2={width - chartPadRight}
                  y2={y}
                  stroke="#4a6080"
                  strokeWidth="1"
                  opacity={tick === 0 ? 0.55 : 0.28}
                />
                <text
                  x={chartPadLeft - 6}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="10"
                  fill="#9eb2d7"
                >
                  {label}
                </text>
              </g>
            );
          })}
        </g>
      ) : null}
      {secondaryYAxisTicks.length > 0 ? (
        <g>
          {secondaryYAxisTicks.map((tick, ti) => {
            const y = baselineY - (tick / secondaryMax) * posPlotHeight;
            const label = secondaryYTickLabels[ti] ?? String(tick);
            return (
              <g key={`y2-${tick}`}>
                <text
                  x={barsEndX + 6}
                  y={y + 3}
                  textAnchor="start"
                  fontSize="10"
                  fill="#9eb2d7"
                >
                  {label}
                </text>
              </g>
            );
          })}
        </g>
      ) : null}
      {legend?.length ? (
        <g>
          {legend.map((entry, li) => {
            const step = Math.min(
              148,
              Math.floor((width - chartPadLeft - chartPadRight) / legend.length),
            );
            const x = chartPadLeft + li * step;
            return (
              <g key={entry.label}>
                {entry.fill ? (
                  <rect x={x} y={10} width={10} height={10} fill={entry.fill} />
                ) : entry.stroke ? (
                  <line
                    x1={x}
                    y1={15}
                    x2={x + 14}
                    y2={15}
                    stroke={entry.stroke}
                    strokeWidth="1.5"
                    strokeDasharray={entry.strokeDasharray ?? "4 3"}
                  />
                ) : null}
                <text
                  x={x + (entry.fill || entry.stroke ? 16 : 0)}
                  y={19}
                  fill="#9eb2d7"
                  fontSize="11"
                >
                  {entry.label}
                </text>
              </g>
            );
          })}
        </g>
      ) : null}
      {hasDiverging ? (
        <line
          x1={chartPadLeft - 4}
          y1={baselineY}
          x2={width - chartPadRight + 4}
          y2={baselineY}
          stroke="#4a6080"
          strokeWidth="1"
        />
      ) : null}
      {data.map((r, i) => {
        const stackSum = r.stack?.length ? r.stack.reduce((s, seg) => s + seg.value, 0) : 0;
        const belowSum = r.stackBelow?.length
          ? r.stackBelow.reduce((s, seg) => s + seg.value, 0)
          : 0;
        const posTotal = barPosTotals[i] ?? r.value;
        const totalH = hasDiverging
          ? Math.max(2, (posTotal / maxPos) * posPlotHeight)
          : Math.max(2, (posTotal / maxPos) * plotHeight);
        const belowH =
          hasDiverging && belowSum > 0
            ? Math.max(2, (belowSum / maxNeg) * negPlotHeight)
            : 0;
        const x = barsStartX + i * (barW + gap);
        const baseY = hasDiverging ? baselineY : plotBottom;
        const secondaryVal =
          r.secondaryValue != null && Number.isFinite(r.secondaryValue) ? r.secondaryValue : null;
        const groupInnerGap = useGroupedSecondary && barW >= 5 ? Math.min(2, barW * 0.15) : 0;
        const twinW = useGroupedSecondary
          ? Math.max(0.5, (barW - groupInnerGap) / 2)
          : barW;
        const primaryX = x;
        const secondaryX = x + twinW + groupInnerGap;
        const secondaryH =
          secondaryVal != null && secondaryVal > 0
            ? Math.max(
                2,
                (secondaryVal / secondaryMax) * (hasDiverging ? posPlotHeight : plotHeight),
              )
            : 0;
        const secondaryFill =
          r.secondaryFill ??
          (typeof r.fill === "string" && r.fill.startsWith("#")
            ? `${r.fill}55`
            : "rgba(49, 196, 141, 0.35)");

        const rects =
          r.stack && r.stack.length > 0 && stackSum > 0
            ? (() => {
                if (stackOverlay) {
                  // Draw largest (first) behind; later segments sit on top from the same baseline.
                  const ordered = r.stack!.map((seg, si) => ({ seg, si }));
                  return ordered.map(({ seg, si }) => {
                    const segH = Math.max(0.5, (seg.value / maxPos) * (hasDiverging ? posPlotHeight : plotHeight));
                    const yTop = baseY - segH;
                    const nextVal =
                      si + 1 < r.stack!.length ? r.stack![si + 1]!.value : 0;
                    const nextH =
                      nextVal > 0
                        ? (nextVal / maxPos) * (hasDiverging ? posPlotHeight : plotHeight)
                        : 0;
                    const ringH = segH - nextH;
                    const segCount = Math.round(seg.value);
                    const segTitle =
                      barTooltip === undefined
                        ? seg.label
                          ? `${seg.label}: ${segCount}`
                          : onBarClick
                            ? `Segment ${si + 1}: click to open Raw Events (middle-click / Ctrl/Cmd+click: new tab).`
                            : `Segment ${si + 1}: ${segCount}`
                        : typeof barTooltip === "function"
                          ? barTooltip(r, i, si)
                          : barTooltip;
                    const showSegLabel =
                      !hideStackSegmentLabels && seg.value > 0 && ringH >= 14;
                    const showSegName =
                      !hideStackSegmentLabels && Boolean(seg.label) && ringH >= 26;
                    const labelY = yTop + Math.min(ringH, segH) / 2;
                    return (
                      <g key={`up-${si}`}>
                        <title>{segTitle}</title>
                        <rect
                          x={x}
                          y={yTop}
                          width={barW}
                          height={segH}
                          fill={seg.fill}
                          style={{ cursor: onBarClick ? "pointer" : undefined }}
                          onClick={onBarClick ? (e) => onBarClick(r, i, si, e) : undefined}
                          onAuxClick={
                            onBarClick
                              ? (e) => {
                                  if (e.button !== 1) return;
                                  onBarClick(r, i, si, e);
                                }
                              : undefined
                          }
                        />
                        {showSegLabel ? (
                          <text
                            x={x + barW / 2}
                            y={showSegName ? labelY - 2 : labelY + 3}
                            textAnchor="middle"
                            fontSize="9"
                            fill="#dce6ff"
                            pointerEvents="none"
                          >
                            {segCount}
                            {showSegName ? (
                              <tspan x={x + barW / 2} dy={10} fontSize="8" fill="#c8d8f5">
                                {seg.label}
                              </tspan>
                            ) : null}
                          </text>
                        ) : null}
                      </g>
                    );
                  });
                }
                let yBottom = baseY;
                return r.stack!.map((seg, si) => {
                  const segH = (seg.value / stackSum) * totalH;
                  yBottom -= segH;
                  const segCount = Math.round(seg.value);
                  const segTitle =
                    barTooltip === undefined
                      ? seg.label
                        ? `${seg.label}: ${segCount}`
                        : onBarClick
                          ? `Segment ${si + 1}: click to open Raw Events (middle-click / Ctrl/Cmd+click: new tab).`
                          : `Segment ${si + 1}: ${segCount}`
                      : typeof barTooltip === "function"
                        ? barTooltip(r, i, si)
                        : barTooltip;
                  const showSegLabel =
                    !hideStackSegmentLabels && seg.value > 0 && segH >= 14;
                  const showSegName =
                    !hideStackSegmentLabels && Boolean(seg.label) && segH >= 26;
                  const segMidY = yBottom + segH / 2;
                  return (
                    <g key={`up-${si}`}>
                      <title>{segTitle}</title>
                      <rect
                        x={x}
                        y={yBottom}
                        width={barW}
                        height={Math.max(segH, 0.5)}
                        fill={seg.fill}
                        style={{ cursor: onBarClick ? "pointer" : undefined }}
                        onClick={onBarClick ? (e) => onBarClick(r, i, si, e) : undefined}
                        onAuxClick={
                          onBarClick
                            ? (e) => {
                                if (e.button !== 1) return;
                                onBarClick(r, i, si, e);
                              }
                            : undefined
                        }
                      />
                      {showSegLabel ? (
                        <text
                          x={x + barW / 2}
                          y={showSegName ? segMidY - 2 : segMidY + 3}
                          textAnchor="middle"
                          fontSize="9"
                          fill="#dce6ff"
                          pointerEvents="none"
                        >
                          {segCount}
                          {showSegName ? (
                            <tspan x={x + barW / 2} dy={10} fontSize="8" fill="#c8d8f5">
                              {seg.label}
                            </tspan>
                          ) : null}
                        </text>
                      ) : null}
                    </g>
                  );
                });
              })()
            : posTotal > 0
              ? [
                  <rect
                    key="single"
                    x={useGroupedSecondary ? primaryX : x}
                    y={baseY - totalH}
                    width={useGroupedSecondary ? twinW : barW}
                    height={totalH}
                    fill={r.fill ?? "#5aa2ff"}
                    style={{ cursor: onBarClick ? "pointer" : undefined }}
                    onClick={onBarClick ? (e) => onBarClick(r, i, undefined, e) : undefined}
                    onAuxClick={
                      onBarClick
                        ? (e) => {
                            if (e.button !== 1) return;
                            onBarClick(r, i, undefined, e);
                          }
                        : undefined
                    }
                  />,
                ]
              : [];

        const secondaryRect =
          useGroupedSecondary && secondaryH > 0 ? (
            <rect
              key="secondary"
              x={secondaryX}
              y={baseY - secondaryH}
              width={twinW}
              height={secondaryH}
              fill={secondaryFill}
              style={{ cursor: onBarClick ? "pointer" : undefined }}
              onClick={onBarClick ? (e) => onBarClick(r, i, 1, e) : undefined}
              onAuxClick={
                onBarClick
                  ? (e) => {
                      if (e.button !== 1) return;
                      onBarClick(r, i, 1, e);
                    }
                  : undefined
              }
            />
          ) : null;

        const aboveSegCount = r.stack?.length ?? (posTotal > 0 ? 1 : 0);
        const belowRects =
          r.stackBelow && r.stackBelow.length > 0 && belowSum > 0
            ? (() => {
                let yTop = baselineY;
                return r.stackBelow!.map((seg, si) => {
                  const segH = (seg.value / belowSum) * belowH;
                  const segCount = Math.round(seg.value);
                  const segIndex = aboveSegCount + si;
                  const segTitle =
                    typeof barTooltip === "function"
                      ? barTooltip(r, i, segIndex)
                      : seg.label
                        ? `${seg.label}: ${segCount}`
                        : `Below: ${segCount}`;
                  const showSegLabel = seg.value > 0 && segH >= 12;
                  const node = (
                    <g key={`down-${si}`}>
                      <title>{segTitle}</title>
                      <rect
                        x={x}
                        y={yTop}
                        width={barW}
                        height={Math.max(segH, 0.5)}
                        fill={seg.fill}
                        style={{ cursor: onBarClick ? "pointer" : undefined }}
                        onClick={onBarClick ? (e) => onBarClick(r, i, segIndex, e) : undefined}
                        onAuxClick={
                          onBarClick
                            ? (e) => {
                                if (e.button !== 1) return;
                                onBarClick(r, i, segIndex, e);
                              }
                            : undefined
                        }
                      />
                      {showSegLabel ? (
                        <text
                          x={x + barW / 2}
                          y={yTop + segH / 2 + 3}
                          textAnchor="middle"
                          fontSize="9"
                          fill="#ffd4d4"
                          pointerEvents="none"
                        >
                          −{segCount}
                        </text>
                      ) : null}
                    </g>
                  );
                  yTop += segH;
                  return node;
                });
              })()
            : [];

        const h = Math.max(totalH, secondaryH);
        const y = baseY - h;
        const lineHeight = valueLabelFontSize + 1;
        /** Prefer just above this bar; never below the reserved band above the plot. */
        const idealValueLabelY = y - valueLabelPad;
        const valueLabelY = Math.max(
          valueLabelsOriginY + valueLabelFontSize,
          Math.min(idealValueLabelY, chartTop - valueLabelPad),
        );
        const labelLines =
          r.valueLabelLines?.length && r.valueLabelLines.some((s) => s.trim() !== "")
            ? r.valueLabelLines.filter((s) => s.trim() !== "")
            : null;
        const valueLabel =
          r.valueLabel ??
          (percent ? fmtPct(r.value) : String(Math.round(r.value * 10) / 10));
        const axisSource = verbatimAxisLabels ? r.label : gameLabel(r.label);
        const axisLines =
          r.axisLabelLines?.length && r.axisLabelLines.some((s) => s.trim() !== "")
            ? r.axisLabelLines.map((s) => trimLabel(s.trim(), axisLabelMaxChars))
            : null;
        const singleLineForTitle = labelLines?.length ? labelLines.join(" · ") : valueLabel;
        const defaultTitle = `${axisSource}: ${singleLineForTitle}`;
        const titleText =
          (r.stack?.length && stackSum > 0) || belowSum > 0
            ? undefined
            : barTooltip === undefined
              ? onBarClick
                ? `${defaultTitle} — Click: open Raw Events (filtered). Middle-click / Ctrl/Cmd+click: new tab.`
                : defaultTitle
              : typeof barTooltip === "function"
                ? barTooltip(r, i)
                : barTooltip;

        const axisPivotX = x + barW / 2;
        const axisPivotY = rotateAxisLabels ? plotBottom + 4 : height - 6;

        return (
          <g key={i}>
            {titleText ? <title>{titleText}</title> : null}
            {rects}
            {secondaryRect}
            {belowRects}
            {highlightIndex === i && (
              <>
                <line
                  x1={x + barW / 2}
                  y1={chartTop + 4}
                  x2={x + barW / 2}
                  y2={plotBottom}
                  stroke="#ffffff"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                  opacity="0.9"
                />
                <rect
                  x={x - 1}
                  y={y - 1}
                  width={barW + 2}
                  height={h + 2}
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="1.5"
                />
                <text
                  x={x + barW / 2}
                  y={chartTop}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#ffffff"
                >
                  {highlightLabel ?? "Now (local)"}
                </text>
              </>
            )}
            {showValueLabels && labelLines ? (
              <text
                x={x + barW / 2}
                y={Math.max(
                  valueLabelsOriginY + valueLabelFontSize,
                  valueLabelY - (labelLines.length - 1) * lineHeight,
                )}
                textAnchor="middle"
                fontSize={valueLabelFontSize}
                fill="#dce6ff"
              >
                {labelLines.map((ln, li) => (
                  <tspan key={li} x={x + barW / 2} dy={li === 0 ? 0 : lineHeight}>
                    {ln}
                  </tspan>
                ))}
              </text>
            ) : showValueLabels ? (
              <text
                x={x + barW / 2}
                y={valueLabelY}
                textAnchor="middle"
                fontSize={valueLabelFontSize}
                fill="#dce6ff"
              >
                {valueLabel}
              </text>
            ) : null}
            {axisLines ? (
              <text
                x={axisPivotX}
                y={axisPivotY}
                textAnchor={rotateAxisLabels ? "end" : "middle"}
                dominantBaseline={rotateAxisLabels ? "middle" : undefined}
                fontSize={rotateAxisLabels ? 10 : 11}
                fontWeight={r.axisLabelFontWeight}
                fill={r.axisLabelFill ?? "#9eb2d7"}
                transform={
                  rotateAxisLabels
                    ? `rotate(-90 ${axisPivotX} ${axisPivotY})`
                    : undefined
                }
              >
                {axisLines.map((ln, li) => (
                  <tspan
                    key={li}
                    x={axisPivotX}
                    dy={li === 0 ? 0 : axisLineHeight}
                  >
                    {ln}
                  </tspan>
                ))}
              </text>
            ) : (
              <text
                x={axisPivotX}
                y={axisPivotY}
                textAnchor={rotateAxisLabels ? "end" : "middle"}
                dominantBaseline={rotateAxisLabels ? "middle" : undefined}
                fontSize={rotateAxisLabels ? 10 : 11}
                fontWeight={r.axisLabelFontWeight}
                fill={r.axisLabelFill ?? "#9eb2d7"}
                transform={
                  rotateAxisLabels
                    ? `rotate(-90 ${axisPivotX} ${axisPivotY})`
                    : undefined
                }
              >
                {trimLabel(axisSource, axisLabelMaxChars)}
              </text>
            )}
          </g>
        );
      })}
      {(() => {
        if (maxPos <= 0) return null;
        const scaleH = hasDiverging ? posPlotHeight : plotHeight;
        const baseY = hasDiverging ? baselineY : plotBottom;
        type Pt = { x: number; y: number; value: number };
        const pts: Array<Pt | null> = data.map((r, i) => {
          if (r.referenceValue == null || !Number.isFinite(r.referenceValue) || r.referenceValue < 0) {
            return null;
          }
          const x = barsStartX + i * (barW + gap) + barW / 2;
          const y = baseY - (Math.min(r.referenceValue, maxPos) / maxPos) * scaleH;
          return { x, y, value: r.referenceValue };
        });
        const segments: Pt[][] = [];
        let cur: Pt[] = [];
        for (const p of pts) {
          if (!p) {
            if (cur.length) {
              segments.push(cur);
              cur = [];
            }
            continue;
          }
          cur.push(p);
        }
        if (cur.length) segments.push(cur);
        if (!segments.length) return null;
        return (
          <g pointerEvents="none" aria-hidden>
            {segments.map((seg, si) => (
              <g key={`ref-line-${si}`}>
                {seg.length >= 2 ? (
                  <polyline
                    points={seg.map((p) => `${p.x},${p.y}`).join(" ")}
                    fill="none"
                    stroke="#c5d4f0"
                    strokeWidth="1.75"
                    strokeDasharray="4 3"
                    opacity="0.95"
                  />
                ) : null}
                {seg.map((p, pi) => (
                  <circle
                    key={pi}
                    cx={p.x}
                    cy={p.y}
                    r={2.5}
                    fill="#0b1220"
                    stroke="#c5d4f0"
                    strokeWidth="1.25"
                  />
                ))}
              </g>
            ))}
          </g>
        );
      })()}
    </svg>
  );
  if (!fillWidth) return svg;
  return (
    <div ref={wrapRef} className="w-full min-w-0">
      {svg}
    </div>
  );
}
